import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CreditCard, Banknote, Loader2, FileText, CheckCircle2, AlertTriangle, ArrowRight, UtensilsCrossed, Ticket, Award, X, Receipt, Store, Truck, Clock, Home, Briefcase, ChevronDown, Pencil } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import api from '@/utils/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { formatGs } from '@/utils/helpers';
import { toast } from 'sonner';
import AddressSelectorSheet from '@/components/AddressSelectorSheet';
import OrderConfirmModal from '@/components/OrderConfirmModal';
import OrderSuccessOverlay from '@/components/OrderSuccessOverlay';

const ICON_MAP = { casa: Home, hogar: Home, trabajo: Briefcase, oficina: Briefcase };
function getLabelIcon(label) {
  const lower = (label || '').toLowerCase();
  for (const [key, Icon] of Object.entries(ICON_MAP)) if (lower.includes(key)) return Icon;
  return MapPin;
}

export default function CheckoutPage({ onSetLocation }) {
  const navigate = useNavigate();
  const { items, subtotal, deliveryFee, total, zone, branchId, clearCart, userLocation, orderType, setOrderType, couponCode, setCouponCode, setZone, setUserLocation } = useCart();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  // Address state
  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null); // full address object
  const [addrZone, setAddrZone] = useState(null); // validated zone for selected address
  const [showAddrSelector, setShowAddrSelector] = useState(false);
  const [showRefEdit, setShowRefEdit] = useState(false);
  const [addressRef, setAddressRef] = useState('');
  const [autoResolved, setAutoResolved] = useState(false);

  // Other state
  const [metodo, setMetodo] = useState('efectivo');
  const [nota, setNota] = useState('');
  const [loading, setLoading] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [branchStatus, setBranchStatus] = useState(null);
  const [pickupBranch, setPickupBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [factura, setFactura] = useState({ denominacion: '', ruc: '', email: '' });

  const eligibleSubtotal = items.reduce((s, i) => {
    if (i.promo) return s;
    const addonsCost = i.addons.reduce((a, ad) => a + ad.extra_cost, 0);
    return s + (i.unit_price + addonsCost) * i.quantity;
  }, 0);

  const loyaltyDiscount = (loyalty?.qualified && !appliedCoupon && eligibleSubtotal > 0)
    ? Math.round(eligibleSubtotal * loyalty.discount_percentage / 100) : 0;
  const couponDiscount = appliedCoupon?.discount || 0;
  const totalDiscount = couponDiscount + loyaltyDiscount;

  // Use addrZone for delivery fee if available
  const activeZone = addrZone || zone;
  const effectiveDelivery = orderType === 'pickup' ? 0 : (activeZone ? parseFloat(activeZone.delivery_price) : deliveryFee);
  const finalTotal = subtotal - totalDiscount + effectiveDelivery;

  // Smart address resolution
  useEffect(() => {
    if (!isAuthenticated || orderType !== 'delivery') return;
    api.get('/direcciones').then(async (r) => {
      const addrs = r.data.direcciones || [];
      setAddresses(addrs);

      if (addrs.length === 0) return;

      // Try to auto-select last used address
      const lastId = user?.last_address_id;
      const lastAddr = addrs.find(a => a.id === lastId);
      const target = lastAddr || addrs[0];

      if (target.lat && target.lng) {
        // Auto-validate zone
        try {
          const { data } = await api.post('/zonas-entrega/validar-ubicacion', { lat: target.lat, lng: target.lng });
          if (data.dentro_de_zona) {
            setSelectedAddr(target);
            setAddrZone(data.zona);
            setAddressRef(target.address_text || '');
            setAutoResolved(true);
            // Also set cart zone if not set
            if (!zone) {
              setZone(data.zona);
              setUserLocation({ lat: target.lat, lng: target.lng });
            }
          }
        } catch { /* ignore */ }
      }
    }).catch(() => {});
    api.get('/cupones/mi-fidelidad').then(r => setLoyalty(r.data.fidelidad)).catch(() => {});
    api.get('/loyalty/status').then(r => setLoyaltyStatus(r.data)).catch(() => {});
    api.get('/auth/perfil').then(r => {
      const u = r.data.usuario;
      setFactura({ denominacion: u.cliente_denominacion_ruc || '', ruc: u.cliente_ruc || '', email: u.cliente_mail_factura || '' });
    }).catch(() => {});
    api.get('/sucursales').then(r => { const b = r.data.sucursales || []; setBranches(b); if (b.length > 0) setPickupBranch(b[0].id); }).catch(() => {});
  }, [isAuthenticated, orderType]); // eslint-disable-line

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/carrito'); return; }
    // Don't redirect to menu if we just created an order (successOrder is set)
    if (items.length === 0 && !successOrder) { navigate('/menu'); return; }
  }, [authLoading, isAuthenticated, items.length, navigate, successOrder]);

  // Check branch status
  const activeBranchId = addrZone?.branch_id || branchId;
  useEffect(() => {
    if (!activeBranchId || orderType !== 'delivery') return;
    const check = () => api.get(`/sucursales/${activeBranchId}/status`).then(r => setBranchStatus(r.data)).catch(() => {});
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [activeBranchId, orderType]);

  const handleAddrSelect = (addr, zone) => {
    setSelectedAddr(addr);
    setAddrZone(zone);
    setAddressRef(addr.address_text || '');
    setShowAddrSelector(false);
    if (zone) {
      setZone(zone);
      if (addr.lat && addr.lng) setUserLocation({ lat: addr.lat, lng: addr.lng });
    }
    // Update last_address_id
    api.put('/auth/perfil', { last_address_id: addr.id }).catch(() => {});
  };

  const handleNewLocation = () => {
    setShowAddrSelector(false);
    onSetLocation();
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true); setCouponError('');
    try {
      const { data } = await api.post('/cupones/validar', {
        code: couponCode.trim(),
        branch_id: activeBranchId,
        eligible_subtotal: eligibleSubtotal,
      });
      setAppliedCoupon(data.cupon);
    } catch (err) { setCouponError(err.response?.data?.error || 'Cupón inválido'); setAppliedCoupon(null); }
    setCouponLoading(false);
  };
  const removeCoupon = () => { setAppliedCoupon(null); setCouponCode(''); setCouponError(''); };

  const canOrder = useMemo(() => {
    if (orderType === 'pickup') return !!pickupBranch;
    if (!selectedAddr) return false;
    if (!addrZone && !zone) return false;
    return true;
  }, [orderType, pickupBranch, selectedAddr, addrZone, zone]);

  // Step 1: Open confirm modal (validates first)
  const handleReviewOrder = () => {
    if (orderType === 'delivery' && !selectedAddr) { toast.error('Seleccioná una dirección de entrega.'); return; }
    if (orderType === 'delivery' && !addrZone && !zone) { toast.error('Tu dirección no está en zona de cobertura.'); return; }
    if (orderType === 'delivery' && !(addressRef || '').trim()) { toast.error('Ingresá tu dirección escrita para el repartidor.'); setShowRefEdit(true); return; }
    if (orderType === 'pickup' && !pickupBranch) { toast.error('Seleccioná una sucursal.'); return; }
    setShowConfirmModal(true);
  };

  // Step 2: Actually send to backend (called from OrderConfirmModal)
  const handleOrder = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        order_type: orderType,
        metodo_pago: metodo,
        nota_general: nota || null,
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        factura_denominacion: factura.denominacion || null,
        factura_ruc: factura.ruc || null,
        factura_email: factura.email || null,
        items: items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          addon_ids: i.addons.map(a => a.id),
          variant_id: i.variant_id || null,
          variant_name: i.variant_name || null,
          variant_price: i.variant_price || null,
        })),
      };
      if (orderType === 'delivery') {
        payload.address_id = selectedAddr.id;
        payload.address_text = addressRef.trim() || selectedAddr.address_text || selectedAddr.address_line || '';
      } else {
        payload.pickup_branch_id = pickupBranch;
      }
      const { data } = await api.post('/pedidos', payload);
      clearCart();
      setShowConfirmModal(false);
      setSuccessOrder({ ...data.pedido, branch_name: activeZone?.branch_name });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear el pedido.');
    } finally { setLoading(false); }
  }, [orderType, metodo, nota, appliedCoupon, factura, items, selectedAddr, addressRef, pickupBranch, clearCart, activeZone]);

  // Compute excluded items for the confirm modal
  const excludedItems = items.filter(i => i.excluded_from_discount);
  const eligibleItems = items.filter(i => !i.excluded_from_discount);

  const AddrIcon = selectedAddr ? getLabelIcon(selectedAddr.label) : MapPin;
  const hasValidAddress = selectedAddr && (addrZone || zone);

  if (authLoading) return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 size={24} className="animate-spin text-[#E84C1C]" /></div>;

  return (
    <div data-testid="checkout-page" className="pb-nav">
      <div className="px-4 pt-4">
        <h2 className="text-xl font-bold">Checkout</h2>
        <p className="text-sm text-zinc-500">Revisá tu pedido antes de confirmar</p>
      </div>

      {/* Order type selector */}
      <section className="mt-4 px-4">
        <div className="flex gap-2 p-1 bg-zinc-900 rounded-xl border border-zinc-800/50">
          <button data-testid="checkout-type-delivery" onClick={() => setOrderType('delivery')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${orderType === 'delivery' ? 'bg-[#E84C1C] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}>
            <Truck size={16} /> Entrega a domicilio
          </button>
          <button data-testid="checkout-type-pickup" onClick={() => setOrderType('pickup')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${orderType === 'pickup' ? 'bg-[#E84C1C] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}>
            <Store size={16} /> Retiro en local
          </button>
        </div>
      </section>

      {/* DELIVERY: Smart address section */}
      {orderType === 'delivery' && (
        <section className="mt-4 px-4">
          {hasValidAddress ? (
            /* Resolved address - compact card */
            <div data-testid="checkout-resolved-address" className="p-4 rounded-2xl bg-emerald-500/8 border border-emerald-500/20">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <AddrIcon size={20} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Entregar en: {selectedAddr.label || 'Mi dirección'}</span>
                    <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5 truncate">
                    {addressRef || selectedAddr.address_text || selectedAddr.address_line}
                    {activeZone?.branch_name && <span className="text-zinc-500"> · {activeZone.branch_name}</span>}
                  </p>
                </div>
                <button
                  data-testid="checkout-change-addr"
                  onClick={() => setShowAddrSelector(true)}
                  className="text-xs text-[#E84C1C] font-medium px-3 py-1.5 rounded-lg bg-[#E84C1C]/10 hover:bg-[#E84C1C]/15 transition-colors shrink-0"
                >
                  Cambiar
                </button>
              </div>
              {/* Address text - required for delivery */}
              {!(addressRef || '').trim() ? (
                <div className="mt-2">
                  <input
                    data-testid="checkout-address-ref"
                    value={addressRef}
                    onChange={e => setAddressRef(e.target.value)}
                    placeholder="Dirección escrita (ej: Andrade 223 c/ España)"
                    className="w-full bg-zinc-900/50 border border-amber-500/40 rounded-lg px-3 py-2 text-xs placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none"
                  />
                  <p className="text-[10px] text-amber-400 mt-1">* Dirección obligatoria para delivery</p>
                </div>
              ) : !showRefEdit ? (
                <button
                  onClick={() => setShowRefEdit(true)}
                  className="mt-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1"
                >
                  <Pencil size={10} /> Editar dirección
                </button>
              ) : (
                <div className="mt-2">
                  <input
                    data-testid="checkout-address-ref"
                    value={addressRef}
                    onChange={e => setAddressRef(e.target.value)}
                    placeholder="Dirección escrita (ej: Andrade 223 c/ España)"
                    className="w-full bg-zinc-900/50 border border-zinc-800/50 rounded-lg px-3 py-2 text-xs placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none"
                  />
                </div>
              )}
              {/* Delivery info */}
              <div className="flex gap-3 mt-3">
                <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <Truck size={12} className="text-[#E84C1C]" />
                  <span>Envío: <span className="text-zinc-200 font-medium">{formatGs(activeZone?.delivery_price || 0)}</span></span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <Clock size={12} className="text-[#E84C1C]" />
                  <span>~{activeZone?.estimated_delivery_time_minutes || 30} min</span>
                </div>
              </div>
            </div>
          ) : selectedAddr && !addrZone && !zone ? (
            /* Address selected but out of zone */
            <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-3">
                <AlertTriangle size={18} className="text-amber-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-300">Dirección fuera de zona de cobertura</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{selectedAddr.label}: {selectedAddr.address_line}</p>
                </div>
                <button onClick={() => setShowAddrSelector(true)} className="text-xs text-[#E84C1C] font-medium px-3 py-1.5 rounded-lg bg-[#E84C1C]/10">Cambiar</button>
              </div>
            </div>
          ) : (
            /* No address yet - show selector button */
            <button
              data-testid="checkout-select-addr-btn"
              onClick={() => setShowAddrSelector(true)}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-dashed border-[#E84C1C]/30 bg-[#E84C1C]/5 hover:bg-[#E84C1C]/10 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-[#E84C1C]/15 flex items-center justify-center">
                <MapPin size={20} className="text-[#E84C1C]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold">Seleccioná tu dirección</p>
                <p className="text-xs text-zinc-500 mt-0.5">Elegí una dirección guardada o nueva ubicación</p>
              </div>
              <ChevronDown size={18} className="text-zinc-500" />
            </button>
          )}
        </section>
      )}

      {/* Branch status warning - delivery only */}
      {orderType === 'delivery' && branchStatus && !branchStatus.is_open_now && (
        <div data-testid="branch-closed-banner" className="mx-4 mt-3 flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle size={18} className="text-amber-400 shrink-0" />
          <p className="text-sm text-amber-300">{branchStatus.message}</p>
        </div>
      )}

      {/* Pickup: branch selector */}
      {orderType === 'pickup' && (
        <section className="mt-4 px-4 space-y-3">
          <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2"><Store size={16} className="text-[#E84C1C]" /> Sucursal de retiro</h3>
          <div className="space-y-2">
            {branches.map(b => (
              <button key={b.id} data-testid={`pickup-branch-${b.id}`} onClick={() => setPickupBranch(b.id)}
                className={`w-full text-left p-3 rounded-xl border transition-colors ${pickupBranch === b.id ? 'bg-[#E84C1C]/10 border-[#E84C1C]/30' : 'bg-zinc-900/50 border-zinc-800/50 hover:border-zinc-700'}`}>
                <p className="text-sm font-medium">{b.name}</p>
                {b.address && <p className="text-xs text-zinc-400">{b.address}</p>}
              </button>
            ))}
          </div>
          <div data-testid="pickup-estimated-time" className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <Clock size={16} className="text-amber-400" /><p className="text-sm text-amber-300">Tiempo estimado: <span className="font-semibold">35 minutos</span></p>
          </div>
        </section>
      )}

      {/* Payment */}
      <section className="mt-6 px-4">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2"><CreditCard size={16} className="text-[#E84C1C]" /> Método de pago</h3>
        <div className="flex gap-3">
          {[{ id: 'efectivo', label: 'Efectivo', icon: Banknote }, { id: 'tarjeta_pos', label: 'Tarjeta POS', icon: CreditCard }].map(m => (
            <button key={m.id} data-testid={`checkout-pay-${m.id}`} onClick={() => setMetodo(m.id)}
              className={`flex-1 flex items-center gap-2 p-3 rounded-xl border transition-colors ${metodo === m.id ? 'bg-[#E84C1C]/10 border-[#E84C1C]/30' : 'bg-zinc-900/50 border-zinc-800/50 hover:border-zinc-700'}`}>
              <m.icon size={18} className={metodo === m.id ? 'text-[#E84C1C]' : 'text-zinc-500'} />
              <span className="text-sm font-medium">{m.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Note */}
      <section className="mt-6 px-4">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2"><FileText size={16} className="text-[#E84C1C]" /> Nota (opcional)</h3>
        <Textarea data-testid="checkout-nota" value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej: Sin wasabi, timbre no funciona..." className="bg-zinc-900 border-zinc-800 resize-none" rows={2} />
      </section>

      {/* Billing */}
      <section className="mt-6 px-4">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2"><Receipt size={16} className="text-[#E84C1C]" /> Facturación</h3>
        <div className="space-y-2">
          <input data-testid="checkout-factura-denominacion" value={factura.denominacion} onChange={e => setFactura(p => ({ ...p, denominacion: e.target.value }))} placeholder="Razón social" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="checkout-factura-ruc" value={factura.ruc} onChange={e => setFactura(p => ({ ...p, ruc: e.target.value }))} placeholder="RUC" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none" />
            <input data-testid="checkout-factura-email" value={factura.email} onChange={e => setFactura(p => ({ ...p, email: e.target.value }))} placeholder="Email factura" type="email" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none" />
          </div>
        </div>
      </section>

      {/* Coupon */}
      <section className="mt-6 px-4">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2"><Ticket size={16} className="text-[#E84C1C]" /> Cupón de descuento</h3>
        {appliedCoupon ? (
          <div data-testid="coupon-applied" className="flex items-center gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-400">{appliedCoupon.code}</p>
              <p className="text-xs text-zinc-400">{appliedCoupon.type === 'percentage' ? `${appliedCoupon.value}%` : formatGs(appliedCoupon.value)} · -{formatGs(appliedCoupon.discount)}</p>
            </div>
            <button data-testid="coupon-remove-btn" onClick={removeCoupon} className="text-zinc-400 hover:text-white"><X size={16} /></button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input data-testid="coupon-input" value={couponCode} onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }} placeholder="Ej: PRIMERA20" className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none" />
            <button data-testid="coupon-apply-btn" onClick={validateCoupon} disabled={couponLoading || !couponCode.trim()} className="px-4 py-2.5 bg-zinc-800 border border-zinc-700 text-sm font-medium rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-40">{couponLoading ? '...' : 'Aplicar'}</button>
          </div>
        )}
        {couponError && <p data-testid="coupon-error" className="text-xs text-red-400 mt-2">{couponError}</p>}
      </section>

      {/* Loyalty */}
      {loyalty?.qualified && !appliedCoupon && eligibleSubtotal > 0 && (
        <div data-testid="loyalty-badge" className="mx-4 mt-3 flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <Award size={18} className="text-amber-400 shrink-0" />
          <div className="flex-1"><p className="text-sm font-medium text-amber-400">Descuento fidelidad</p><p className="text-xs text-zinc-400">{loyalty.orders_this_month} pedidos · {loyalty.discount_percentage}%</p></div>
          <span className="text-sm font-bold text-amber-400">-{formatGs(loyaltyDiscount)}</span>
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 mx-4 p-4 bg-zinc-900 rounded-xl border border-zinc-800/50 space-y-2">
        {items.map(i => {
          const addonsCost = i.addons.reduce((s, a) => s + a.extra_cost, 0);
          return (
            <div key={i.key} className="flex justify-between text-sm">
              <span className="text-zinc-400 truncate mr-2">{i.quantity}x {i.name}{i.variant_name ? ` (${i.variant_name})` : ''}{i.addons.length > 0 && ` (+${i.addons.length})`}{i.promo && <span className="ml-1 text-[10px] bg-[#E84C1C]/15 text-[#E84C1C] px-1.5 py-0.5 rounded-full">Promo</span>}</span>
              <span className="shrink-0">{formatGs((i.unit_price + addonsCost) * i.quantity)}</span>
            </div>
          );
        })}
        <div className="border-t border-zinc-800 pt-2 mt-2 space-y-1">
          <div className="flex justify-between text-sm"><span className="text-zinc-400">Subtotal</span><span>{formatGs(subtotal)}</span></div>
          {couponDiscount > 0 && <div data-testid="checkout-coupon-discount" className="flex justify-between text-sm"><span className="text-emerald-400">Cupón ({appliedCoupon.code})</span><span className="text-emerald-400">-{formatGs(couponDiscount)}</span></div>}
          {loyaltyDiscount > 0 && <div data-testid="checkout-loyalty-discount" className="flex justify-between text-sm"><span className="text-amber-400">Fidelidad ({loyalty.discount_percentage}%)</span><span className="text-amber-400">-{formatGs(loyaltyDiscount)}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-zinc-400">{orderType === 'pickup' ? 'Retiro' : 'Envío'}</span><span>{orderType === 'pickup' ? 'Gratis' : formatGs(effectiveDelivery)}</span></div>
        </div>
        <div className="border-t border-zinc-800 pt-2 flex justify-between font-semibold text-base"><span>Total</span><span className="text-[#E84C1C]">{formatGs(finalTotal)}</span></div>
      </div>

      <div className="px-4 mt-6 mb-4">
        <button
          data-testid="checkout-confirm-btn"
          onClick={handleReviewOrder}
          disabled={loading || !canOrder || (orderType === 'delivery' && branchStatus && !branchStatus.is_open_now)}
          className="w-full bg-[#E84C1C] text-white font-semibold py-3.5 rounded-full hover:bg-[#d43f12] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(232,76,28,0.3)]"
        >
          {branchStatus && !branchStatus.is_open_now ? 'Sucursal no disponible' : 'Confirmar pedido'}
        </button>
      </div>

      {/* Address selector sheet */}
      <AddressSelectorSheet
        open={showAddrSelector}
        onClose={() => setShowAddrSelector(false)}
        onNewLocation={handleNewLocation}
        onSelect={handleAddrSelect}
      />

      {/* Order review/confirm modal */}
      <OrderConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onEdit={() => setShowConfirmModal(false)}
        onConfirm={handleOrder}
        orderData={{
          orderType, selectedAddr, activeZone, pickupBranch, branches,
          metodo, nota, items, subtotal, couponDiscount, loyaltyDiscount,
          appliedCoupon, loyalty, loyaltyStatus, effectiveDelivery, finalTotal, user,
          addressRef, excludedItems, eligibleItems
        }}
      />

      {/* Success overlay with sound */}
      {successOrder && (
        <OrderSuccessOverlay
          order={successOrder}
          onTrack={() => navigate(`/pedido/${successOrder.id}`)}
          onMenu={() => navigate('/menu')}
        />
      )}
    </div>
  );
}
